<template>
  <!-- Overlay loader enquanto carrega mensagens i18n -->
  <Transition name="fade">
    <div
      v-if="isLoadingLocale"
      class="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <!-- Spinner -->
        <div
          class="w-12 h-12 border-4 border-transparent border-t-white dark:border-t-black rounded-full animate-spin"
        ></div>
        <p class="text-white dark:text-black text-sm font-medium">
          Carregando idioma...
        </p>
      </div>
    </div>
  </Transition>

  <Select v-model="current" @update:modelValue="onChange">
    <SelectTrigger
      class="text-black bg-white border border-gray-300 cursor-pointer w-fit dark:bg-zinc-950 dark:text-white dark:border-zinc-600"
    >
      <div class="flex items-center">
        <!-- Flag always shown -->
        <Icon :name="selectedOption?.icon" class="mr-0 sm:mr-2" />

        <!-- Language name shown only on larger screens -->
        <span class="hidden sm:inline">
          {{ selectedOption?.label }}
        </span>
      </div>
    </SelectTrigger>

    <SelectContent
      class="text-black bg-white border border-gray-200 cursor-pointer dark:bg-zinc-950 dark:text-white dark:border-zinc-700 z-[9999]"
    >
      <SelectGroup>
        <SelectItem
          v-for="lang in languages"
          :key="lang.value"
          :value="lang.value"
          class="cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 focus:bg-gray-100 dark:focus:bg-zinc-800"
        >
          <Icon :name="lang.icon" class="mr-2" />
          {{ lang.label }}
        </SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
</template>

<script setup lang="ts">
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";

import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCookie } from "#app";
import { useLocales, fetchI18nMessages } from "~/composables/useLocales";

// i18n and cookie
const i18n = useI18n();
const localeCookie = useCookie<string>("i18n_locale", {
  maxAge: 60 * 60 * 24 * 365, // 1 ano
  secure: true,
  httpOnly: false,
  sameSite: "lax",
});

// INITIAL LOCALE: vem do cookie, navegador ou fallback PT
const current = ref(localeCookie.value || i18n.locale.value || "pt");

// Estado de loading enquanto carrega mensagens i18n
const isLoadingLocale = ref(false);

// fetch dynamic languages
const { data: reffedLanguages } = useLocales();

const fallbackLanguages = [
  { value: "pt", label: "Português", icon: "circle-flags:pt" },
  { value: "en", label: "English", icon: "circle-flags:gb" },
];

const languages = computed(() =>
  reffedLanguages.value?.length ? reffedLanguages.value : fallbackLanguages
);

const selectedOption = computed(() =>
  languages.value.find((l) => l.value === current.value)
);

// ONLY run when user changes language manually
const onChange = async (val: string) => {
  // Evitar múltiplos cliques enquanto carrega
  if (isLoadingLocale.value) return;

  console.log("[LanguageSelector] Mudando para:", val);
  isLoadingLocale.value = true;

  try {
    // Usa cache se já foi carregado antes
    console.log("[LanguageSelector] Fetching messages para:", val);
    const messages = await fetchI18nMessages(val);
    console.log(
      "[LanguageSelector] Messages recebidas:",
      Object.keys(messages).length > 0 ? "✅ Carregadas" : "❌ Vazias"
    );

    if (Object.keys(messages).length > 0) {
      i18n.setLocaleMessage(val, messages);
      console.log("[LanguageSelector] Mensagens setadas no i18n");
    }
  } catch (err) {
    console.error(
      `[LanguageSelector] Erro ao carregar mensagens para ${val}:`,
      err
    );
    // Fallback: deixar vazio (já existem mensagens carregadas)
  } finally {
    // Mesmo com erro, muda o locale e fecha o loader
    console.log("[LanguageSelector] Setando locale para:", val);
    i18n.locale.value = val;
    localeCookie.value = val; // Persiste no cookie
    current.value = val;

    console.log("[LanguageSelector] Locale atual:", i18n.locale.value);
    console.log("[LanguageSelector] Loader fechado");
    isLoadingLocale.value = false;
  }
};
</script>
